/**
 * Campaign Reports tab — shared constants, formatters and pure helpers for the
 * campaign-level and advertiser-level report page UI.
 *
 * No React in here: the mapping rules (lifecycle → pill, sync state → pill,
 * sync reason → plain English, create-modal row plans, slug/PIN rules) are
 * plain functions so they can be unit-tested and reused by every modal/table.
 */

import type React from 'react';
import type { StatusVariant } from '../../ui';
import type {
  AdvertiserCandidateGroup,
  AdvertiserReportPage,
  AdvertiserSyncReason,
  AdvertiserSyncState,
} from '../../../services/advertiserReportsApi';
import type { AdvertiserCampaignLifecycle, CampaignReportPage, ReportableOrder, ReportWindowMode } from '../../../services/campaignReportsApi';

export type Notify = (kind: 'success' | 'error', title: string, message?: string) => void;

// ---------------------------------------------------------------------------
// URLs & constants
// ---------------------------------------------------------------------------

export const PUBLIC_PAGES_BASE = 'https://www.culturegenesis.com';
export const campaignPageUrl = (slug: string) => `${PUBLIC_PAGES_BASE}/campaigns/${slug}/report`;
export const advertiserPageUrl = (slug: string) => `${PUBLIC_PAGES_BASE}/advertisers/${slug}/reports`;
export const EXPIRY_DAYS_AFTER_FLIGHT_END = 60;
/** Advertiser sync: each child report takes roughly this long to generate (three GAM report jobs). */
export const MINUTES_PER_REPORT = 5;
/** A 'running' sync whose heartbeat is older than this was interrupted (mirrors backend SYNC_STALE_MS). */
export const SYNC_STALE_MS = 5 * 60_000;

export function campaignWindowMode(page: Pick<CampaignReportPage, 'reportingWindowMode' | 'reportStartDate' | 'requestedEndDate' | 'flightStartDate' | 'flightEndDate'>): ReportWindowMode {
  return page.reportingWindowMode ?? (page.reportStartDate === page.flightStartDate && page.requestedEndDate === page.flightEndDate ? 'follow_campaign' : 'fixed');
}

export function campaignManualRefreshBlocker(page: CampaignReportPage, nowMs = Date.now()): string | null {
  if (page.revokedAt) return 'Restore this report before refreshing from GAM.';
  if (page.expired || Date.parse(page.expiresAt) <= nowMs) return 'Extend this report’s expiry before refreshing from GAM.';
  if (page.dataStatus === 'generating') return 'A refresh is already running.';
  return null;
}

/**
 * GAM says every counted line item has finished — the hub card's 'completed'
 * lifecycle (backend hubLifecycleForPage): the advertiser sync's captured
 * lifecycle wins, else a passed flight end, else the pacing status. A takeover
 * that fills early is complete here before its GAM end date.
 */
export function gamDeliveryComplete(page: CampaignReportPage, today: string): boolean {
  if (page.lifecycle) return page.lifecycle === 'completed';
  if (page.flightEndDate && page.flightEndDate < today) return true;
  return (page.pacingSummary?.status ?? page.pacingStatus ?? null) === 'COMPLETED';
}

/** Refresh health is only stale while this page is expected to receive hourly captures. */
export function campaignRefreshState(page: CampaignReportPage, nowMs = Date.now()): {
  phase: 'live' | 'settling' | 'final'; problem: 'error' | 'stale' | null; detail: string;
} {
  const today = new Date(nowMs).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const end = campaignWindowMode(page) === 'fixed' ? page.requestedEndDate : page.flightEndDate;
  const settlementEnd = page.settlementEndDate ?? addDaysYmd(end, 14);
  // Live only while GAM is still delivering: a campaign GAM marks complete
  // settles from that moment, like its hub card and client page.
  const phase = today <= end && !gamDeliveryComplete(page, today) ? 'live'
    : today <= settlementEnd || page.finalized === false ? 'settling' : 'final';
  if (!page.autoRefresh || page.revokedAt || page.expired || Date.parse(page.expiresAt) <= nowMs || today < page.reportStartDate) {
    return { phase, problem: null, detail: '' };
  }
  const fixed = campaignWindowMode(page) === 'fixed';
  const sections: Array<'headline' | 'delivery' | 'dates' | 'order'> = phase === 'final'
    ? fixed ? ['dates', 'order'] : ['headline', 'dates']
    : fixed ? ['headline', 'delivery', 'dates', 'order'] : ['headline', 'delivery', 'dates'];
  const errorSections: Array<'headline' | 'delivery' | 'dates' | 'order' | 'daily'> = [...sections, 'daily'];
  const failed = errorSections.filter((key) => page.refreshHealth?.[key]?.error);
  if (failed.length) return { phase, problem: 'error', detail: failed.map((key) => `${key}: ${page.refreshHealth?.[key]?.error}`).join('; ') };
  const stale = sections.filter((key) => {
    const last = page.refreshHealth?.[key]?.lastSuccessAt ?? (key === 'headline' ? page.headlineDelivery?.capturedAt : undefined) ?? page.lastRefreshAt ?? page.createdAt;
    const lastMs = Date.parse(last);
    return !Number.isFinite(lastMs) || nowMs - lastMs > 90 * 60_000;
  });
  return { phase, problem: stale.length ? 'stale' : null, detail: stale.length ? `No successful ${stale.join(', ')} refresh in the last 90 minutes. Last good data remains available.` : '' };
}

// ---------------------------------------------------------------------------
// Styles shared by the modals
// ---------------------------------------------------------------------------

export const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-1)',
  fontSize: 13.5,
  boxSizing: 'border-box',
};

export const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  display: 'block',
  marginBottom: 4,
};

export const helpStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--fg-4)', marginTop: 4 };

export const summaryStripStyle: React.CSSProperties = {
  background: 'var(--bg-surface-2)',
  borderRadius: 'var(--radius-md)',
  padding: '10px 12px',
  fontSize: 13,
};

export const sectionTitleStyle: React.CSSProperties = { fontWeight: 700, fontSize: 15 };
export const sectionSubtitleStyle: React.CSSProperties = { fontSize: 12.5, color: 'var(--fg-3)' };

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export const fmtInt = (n?: number | null): string =>
  typeof n === 'number' && Number.isFinite(n) ? Math.round(n).toLocaleString('en-US') : '—';

export const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** "Sep 8, 3:05 PM PT" — network-timezone timestamp for sync/pacing captures. */
export const fmtDateTimePt = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} PT`;
};

/** "just now" / "4 min ago" / "3 h ago" / "2 d ago", then a plain date. */
export const fmtRelative = (iso?: string | null, nowMs: number = Date.now()): string => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const minutes = Math.round(Math.max(0, nowMs - t) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return fmtDate(iso);
};

export const addDaysYmd = (ymd: string, days: number): string => {
  const ms = Date.parse(`${ymd}T00:00:00Z`) + days * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
};

export const isYmd = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v);

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------------------
// Slugs & PINs (mirrors of the backend rules, for live validation)
// ---------------------------------------------------------------------------

/** Mirror of the backend slugify (takeoversFirestore.ts) for live URL preview. */
export const localSlugify = (name: string): string =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const MONTH_SLUGS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** Mirror of the backend SLUG_MAX_LENGTH (slugify's cap). */
export const SLUG_MAX_LENGTH = 60;

/**
 * Mirror of the backend defaultCampaignSlug for live URL preview. The name part
 * is cut so the whole slug fits the cap WITH the month suffix, and a hyphen the
 * cut leaves behind is trimmed — a long GAM order name used to lose its month
 * and end in "-", which the backend rejects.
 */
export const defaultCampaignSlug = (orderName: string, reportStartDate: string): string => {
  const stripped = orderName.replace(/^\s*(culture\s*genesis|cg)\s*[|\-–—:]\s*/i, '').trim() || orderName;
  const month = isYmd(reportStartDate)
    ? `${MONTH_SLUGS[Number(reportStartDate.slice(5, 7)) - 1] ?? 'jan'}-${reportStartDate.slice(0, 4)}`
    : '';
  if (!month) return localSlugify(stripped);
  const namePart = localSlugify(stripped)
    .slice(0, SLUG_MAX_LENGTH - month.length - 1)
    .replace(/-+$/, '');
  return localSlugify(`${namePart}-${month}`);
};

/** Mirrors creatorPagesFirestore RESERVED_SLUGS (normalizeSlug refuses these). */
const RESERVED_SLUGS = new Set(['preview', 'new', 'admin', 'api']);
/** Mirrors the backend SLUG_RE. */
const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Admin-facing problem with a (already slugified) slug, or null when the backend will accept it. */
export const slugProblem = (slug: string): string | null => {
  if (slug.length < 3) return 'Slug must be at least 3 characters';
  if (!SLUG_RE.test(slug)) return 'Slug may only contain lowercase letters, numbers and hyphens';
  if (RESERVED_SLUGS.has(slug)) return `“${slug}” is a reserved word — pick a different slug`;
  return null;
};

export { WEAK_PINS, randomPin, pinProblem } from '../../../utils/reportPagePins';

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/** Pill per lifecycle. 'pending' is deliberately NOT used — that palette means Generating / Awaiting data. */
export const LIFECYCLE_PILL: Record<AdvertiserCampaignLifecycle, { variant: StatusVariant; label: string }> = {
  live: { variant: 'live', label: 'Delivering' },
  upcoming: { variant: 'approved', label: 'Upcoming' },
  paused: { variant: 'paused', label: 'Paused' },
  in_work: { variant: 'draft', label: 'In work' },
  completed: { variant: 'paused', label: 'Ended' },
};

/** Display order for counts and sorting (matches the backend picker rank). */
export const LIFECYCLE_ORDER: AdvertiserCampaignLifecycle[] = ['live', 'upcoming', 'paused', 'in_work', 'completed'];

const LIFECYCLE_COUNT_WORD: Record<AdvertiserCampaignLifecycle, string> = {
  live: 'live',
  upcoming: 'upcoming',
  paused: 'paused',
  in_work: 'in work',
  completed: 'ended',
};

/** Lifecycle of a picker row, tolerating rows from an older backend build that only carry `status`. */
export const lifecycleOf = (order: Pick<ReportableOrder, 'lifecycle' | 'status'>): AdvertiserCampaignLifecycle =>
  order.lifecycle ?? (order.status === 'DELIVERING' ? 'live' : 'completed');

/** "3 live · 1 upcoming · 2 ended" — zero buckets omitted. */
export const formatLifecycleCounts = (counts: Partial<Record<AdvertiserCampaignLifecycle, number>>): string => {
  const parts = LIFECYCLE_ORDER.filter((k) => (counts[k] ?? 0) > 0).map((k) => `${counts[k]} ${LIFECYCLE_COUNT_WORD[k]}`);
  return parts.length > 0 ? parts.join(' · ') : '0 campaigns';
};

const PICKER_RANK: Record<AdvertiserCampaignLifecycle, number> = { live: 0, upcoming: 1, paused: 2, in_work: 3, completed: 4 };

/** Same order the backend picker uses: lifecycle rank, then latest end date first (no end date last). */
export const sortPickerRows = <T extends Pick<ReportableOrder, 'lifecycle' | 'status' | 'endDate'>>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => {
    const rank = PICKER_RANK[lifecycleOf(a)] - PICKER_RANK[lifecycleOf(b)];
    if (rank !== 0) return rank;
    return (b.endDate || '9999').localeCompare(a.endDate || '9999');
  });

/** Same rule as the server-side `query` filter (order OR advertiser name), plus an exact order-id match. */
export const matchesQuery = (
  order: Pick<ReportableOrder, 'gamOrderName' | 'advertiserName' | 'gamOrderId'>,
  normalizedQuery: string,
): boolean =>
  !normalizedQuery ||
  order.gamOrderName.toLowerCase().includes(normalizedQuery) ||
  order.advertiserName.toLowerCase().includes(normalizedQuery) ||
  order.gamOrderId === normalizedQuery;

// ---------------------------------------------------------------------------
// Related orders under OTHER GAM advertisers (admin suggestions)
//
// Hub discovery is scoped to the hub's GAM advertiser ids, so a campaign
// re-booked on another seat ("Dr. Squatch – iShowSpeed (Unilever – New Seat
// ID …)" under the Unilever company, Sept 2026) never reaches the hub. The
// picker already holds every live / recently-ended order network-wide, so the
// brand's orders under other advertisers can be found by name, client-side,
// and offered to the admin — nothing here is client-visible until included.
// ---------------------------------------------------------------------------

/** Lowercased alphanumeric tokens: "Dr. Squatch – iShowSpeed" → ['dr', 'squatch', 'ishowspeed']. */
export const tokenizeName = (s: string): string[] =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/** Words that carry no brand meaning at the edges of a company name. */
const NAME_STOPWORDS = new Set([
  'the', 'company', 'co', 'inc', 'llc', 'ltd', 'corp', 'group', 'brands', 'holdings',
  'usa', 'us', 'north', 'america', 'media', 'agency', 'and',
]);

/**
 * Brand terms to look for in order names: each hub label (GAM company name,
 * client label) as tokens, plus the label with a trailing parenthetical dropped
 * ("Coke (TCCC)" → "Coke"); edge stopwords removed; at least 3 letters; deduped.
 */
export const relatedOrderTerms = (labels: readonly string[]): string[][] => {
  const seen = new Set<string>();
  const terms: string[][] = [];
  for (const label of labels) {
    for (const variant of [label, label.replace(/\s*\([^)]*\)\s*$/, '')]) {
      const tokens = tokenizeName(variant);
      while (tokens.length > 0 && NAME_STOPWORDS.has(tokens[0])) tokens.shift();
      while (tokens.length > 0 && NAME_STOPWORDS.has(tokens[tokens.length - 1])) tokens.pop();
      if (tokens.join('').length < 3) continue;
      const key = tokens.join(' ');
      if (seen.has(key)) continue;
      seen.add(key);
      terms.push(tokens);
    }
  }
  return terms;
};

/** Does the order name contain one of the terms as a contiguous run of whole tokens? ("Squatchy" ≠ "Squatch".) */
export const isRelatedOrder = (orderName: string, terms: readonly string[][]): boolean => {
  const tokens = tokenizeName(orderName);
  return terms.some((term) => {
    if (term.length === 0 || term.length > tokens.length) return false;
    for (let i = 0; i + term.length <= tokens.length; i += 1) {
      if (term.every((t, j) => tokens[i + j] === t)) return true;
    }
    return false;
  });
};

/**
 * Picker rows booked under OTHER GAM advertisers whose order name carries one of
 * the hub's brand terms, minus orders the hub already knows (its roster / manual
 * includes). Sorted like the picker: live first, then latest end date.
 */
export const relatedOrdersForHub = (input: {
  groups: readonly AdvertiserCandidateGroup[];
  /** Hub labels to derive brand terms from — GAM company name and client label. */
  labels: readonly string[];
  /** The hub's own GAM advertiser ids (primary + aliases). */
  advertiserIds: readonly string[];
  knownOrderIds?: ReadonlySet<string>;
}): ReportableOrder[] => {
  const terms = relatedOrderTerms(input.labels);
  if (terms.length === 0) return [];
  const own = new Set(input.advertiserIds.filter(Boolean));
  const rows: ReportableOrder[] = [];
  for (const group of input.groups) {
    if (group.advertiserId && own.has(group.advertiserId)) continue;
    for (const order of group.orders) {
      if (order.advertiserId && own.has(order.advertiserId)) continue;
      if (input.knownOrderIds?.has(order.gamOrderId)) continue;
      if (isRelatedOrder(order.gamOrderName, terms)) rows.push(order);
    }
  }
  return sortPickerRows(rows);
};

// ---------------------------------------------------------------------------
// Advertiser create modal — what the sync will do with each order
// ---------------------------------------------------------------------------

export type CreateRowPlan = 'link' | 'linked_elsewhere' | 'create' | 'needs_window' | 'in_work' | 'hidden_upcoming';

export const CREATE_ROW_PLAN_LABEL: Record<CreateRowPlan, string> = {
  link: 'will link existing page',
  linked_elsewhere: 'linked to another advertiser page — left alone',
  create: 'will create + generate',
  needs_window: 'needs reporting window',
  in_work: 'in work — admin only',
  hidden_upcoming: 'hidden while upcoming campaigns are off',
};

/** Mirrors planAdvertiserSync's decision order: existing page → in work → visibility → dates. */
export const planCreateRow = (
  order: Pick<ReportableOrder, 'lifecycle' | 'status' | 'startDate' | 'endDate' | 'existingPageSlug' | 'advertiserPageSlug'>,
  showUpcoming: boolean,
): CreateRowPlan => {
  if (order.existingPageSlug) return order.advertiserPageSlug ? 'linked_elsewhere' : 'link';
  const lifecycle = lifecycleOf(order);
  if (lifecycle === 'in_work') return 'in_work';
  if (lifecycle === 'upcoming' && !showUpcoming) return 'hidden_upcoming';
  if (!order.startDate || !order.endDate) return 'needs_window';
  return 'create';
};

// ---------------------------------------------------------------------------
// Sync state
// ---------------------------------------------------------------------------

/** Plain-English copy for every skip / warning reason the sync records. */
export const SYNC_REASON_LABEL: Record<AdvertiserSyncReason, string> = {
  needs_reporting_window: 'Needs a reporting window (unlimited flight)',
  awaiting_approval: 'Awaiting GAM approval',
  not_visible: 'Hidden by page settings',
  create_failed: 'Page creation failed',
  month_slice_linked: 'Linked page covers a month, not the full flight',
  discovery_truncated: 'GAM returned 500+ orders — some may be missing',
  duplicate_page_revoked: 'Duplicate page revoked',
  cross_advertiser_include: 'Includes an order from another advertiser',
  order_lookup_failed: 'Order lookup failed',
  linked_elsewhere: 'Linked to another advertiser page',
};

export const syncReasonLabel = (reason: string): string =>
  (SYNC_REASON_LABEL as Record<string, string>)[reason] ?? reason.replace(/_/g, ' ');

/**
 * True while a sync is genuinely live: the server's in-process flag, or a
 * 'running' doc whose heartbeat is fresh. A 'running' doc with a stale
 * heartbeat is an interrupted run — it must not keep the table polling forever.
 */
export const isSyncLive = (
  page: Pick<AdvertiserReportPage, 'syncing'> & { sync?: Pick<AdvertiserSyncState, 'status' | 'heartbeatAt' | 'startedAt'> | null },
  nowMs: number = Date.now(),
): boolean => {
  if (page.syncing) return true;
  const sync = page.sync;
  if (!sync || sync.status !== 'running') return false;
  const beat = Date.parse(sync.heartbeatAt || sync.startedAt || '');
  return Number.isFinite(beat) && nowMs - beat < SYNC_STALE_MS;
};

export interface SyncPillSpec {
  variant: StatusVariant;
  label: string;
  /** Tooltip (title attr) — plain-English detail. */
  title?: string;
}

/**
 * Warnings that record a confirmed admin choice rather than a problem — listed
 * in the Manage "Last sync" details, never counted as "Needs attention" (the
 * sync re-emits cross_advertiser_include on every run for a confirmed include,
 * which would otherwise keep the hub amber forever).
 */
export const INFORMATIONAL_WARNINGS: ReadonlySet<AdvertiserSyncReason> = new Set<AdvertiserSyncReason>(['cross_advertiser_include']);

/** One-line human descriptions of a sync's skipped orders and warnings (tooltips, summaries). */
export const syncIssueLines = (sync: Pick<AdvertiserSyncState, 'skipped' | 'warnings'>): string[] => [
  ...(sync.skipped ?? []).map((s) => `Skipped · ${s.gamOrderName || s.gamOrderId}: ${syncReasonLabel(s.reason)}`),
  ...(sync.warnings ?? []).map((w) => {
    const subject = w.slug || w.gamOrderId;
    return `Warning${subject ? ` · ${subject}` : ''}: ${syncReasonLabel(w.reason)}${w.detail ? ` (${w.detail})` : ''}`;
  }),
];

const RUNNING_STEP_LABEL: Record<AdvertiserSyncState['step'], string> = {
  idle: 'Syncing…',
  discovering: 'Discovering campaigns…',
  linking: 'Linking pages…',
  creating: 'Creating pages…',
  generating: 'Generating…',
  done: 'Finishing…',
};

/** The Sync column pill: Synced {relative} / Syncing 3 of 7… / Needs attention (n) / Error / Interrupted. */
export const describeSync = (
  page: Pick<AdvertiserReportPage, 'syncing' | 'sync'>,
  nowMs: number = Date.now(),
): SyncPillSpec => {
  const sync = page.sync;
  if (!sync) return { variant: 'draft', label: 'Not synced yet' };
  if (isSyncLive(page, nowMs)) {
    const label =
      sync.step === 'generating' && sync.total > 0
        ? `Syncing ${Math.min(sync.current + 1, sync.total)} of ${sync.total}…`
        : RUNNING_STEP_LABEL[sync.step] ?? 'Syncing…';
    const by = sync.requestedBy ? ` — started by ${sync.requestedBy}` : '';
    return { variant: 'pending', label, title: `Reports generate one at a time (~${MINUTES_PER_REPORT} min each)${by}` };
  }
  if (sync.status === 'running') {
    return {
      variant: 'flagged',
      label: 'Interrupted',
      title: 'The last sync stopped before finishing (server restart?) — click Sync now to run it again.',
    };
  }
  if (sync.status === 'error') {
    return {
      variant: 'flagged',
      label: 'Error',
      title: (sync.errors ?? []).join('\n') || 'The last sync failed — click Sync now to retry.',
    };
  }
  const issues = syncIssueLines({
    skipped: sync.skipped,
    warnings: (sync.warnings ?? []).filter((w) => !INFORMATIONAL_WARNINGS.has(w.reason)),
  });
  if (issues.length > 0) {
    return { variant: 'pending', label: `Needs attention (${issues.length})`, title: issues.join('\n') };
  }
  if (sync.finishedAt) {
    const by = sync.requestedBy ? ` by ${sync.requestedBy}` : '';
    return { variant: 'ok', label: `Synced ${fmtRelative(sync.finishedAt, nowMs)}`, title: `Last sync ${fmtDateTimePt(sync.finishedAt)}${by}` };
  }
  return { variant: 'draft', label: 'Not synced yet' };
};

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/** Order-insensitive comparison of two id lists. */
export const sameIdSet = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
};

/** Order-sensitive comparison (alias lists — the first id is the primary advertiser). */
export const sameIdList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** Parse the Edit modal's "Brands (comma-separated)" field. Returns an error message instead of a list when invalid. */
export const parseBrandNames = (text: string): { brands: string[] } | { error: string } => {
  const brands = text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (brands.length > 3) return { error: 'At most 3 brands' };
  if (brands.some((b) => b.length > 40)) return { error: 'Each brand must be 40 characters or fewer' };
  return { brands: Array.from(new Set(brands)) };
};
